<template>
  <div>
    <section v-if="superFacilities" class="popular-facilities">
      <p
        v-for="facility of superFacilities"
        :key="facility.slug"
        class="popular"
      >
        <img
          v-if="facilityIcons[facility.slug]"
          loading="lazy"
          class="icon"
          :src="facilityIcons[facility.slug]"
          width="16"
          height="16"
          alt=""
        />{{ facility.name }}
      </p>
    </section>

    <section class="campsite-features">
      <div
        v-for="(facilityGroup, index) of filteredFacilityGroups"
        :key="index"
        class="box"
        :class="[index % 2 === 0 ? 'odd' : 'even']"
      >
        <h3>{{ facilityGroup.name }}</h3>
        <span
          v-for="facility of getActiveFacilitiesOfGroup(facilityGroup)"
          :key="facility.slug"
          class="facility"
          :class="isAvailable(facility.showAsActive)"
        >
          {{ facility.name }}
          <pu-popover v-if="showPopover(facility)">
            <template #content>
              <h4>{{ facility.name }}</h4>
              <img
                v-if="facility.photo && facility.photo.master_image"
                loading="lazy"
                :src="`/_cfi/cdn-cgi/image/f=auto,fit=cover,g=auto,quality=80,w=360,h=270${facility.photo.master_image}`"
                :srcset="`/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${facility.photo.master_image} 360w, /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=70,w=720,h=540${facility.photo.master_image} 720w, /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=60,w=1080,h=810${facility.photo.master_image} 1080w`"
                :alt="facility.name"
                width="300"
                height="225"
                class="feature-photo"
              />
              <p v-if="facility.description" class="feature-description">
                {{ facility.description }}
              </p>
              <p v-if="facility.notes" class="feature-description">
                {{ facility.notes }}
              </p>
            </template>
          </pu-popover>
        </span>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import {
  CampsiteFacility,
  CampsiteFacilityGroup,
} from '../../../campsite-details/types'

@Component({
  async setup(props) {
    const iconModules = import.meta.glob<{ default: string }>(
      '@/assets/facility-icons/*.svg',
      {
        query: '?url',
        eager: false,
      },
    )
    const facilityIcons = {}
    if (props.superFacilities?.length) {
      await Promise.all(
        props.superFacilities.map(async (facility) => {
          try {
            const iconPath = `/assets/facility-icons/${facility.slug}.svg`
            const module = iconModules[iconPath]
            if (module) {
              const imported = await module()
              // Use direct assignment with ref
              facilityIcons[facility.slug] = imported.default
            }
          } catch (err) {
            console.error(err)
          }
        }),
      )
    }
    return {
      facilityIcons,
      iconModules,
    }
  },
})
class CampsiteFeatures extends Vue {
  @Prop()
  facilitiesGroups: CampsiteFacilityGroup[]

  @Prop()
  superFacilities: CampsiteFacility[]

  facilityIcons: Record<string, string>
  iconModules: Record<string, string>

  isAvailable(isFacilityAvailable) {
    return isFacilityAvailable ? 'active' : 'inactive'
  }

  get filteredFacilityGroups() {
    return this.facilitiesGroups?.filter((group) => !group.isCovid)
  }

  getActiveFacilitiesOfGroup(
    facilityGroup: CampsiteFacilityGroup,
  ): CampsiteFacility[] {
    return facilityGroup.facilities.filter((facility) => facility.isActive)
  }

  showPopover(facility) {
    if (facility.notes && facility.notes !== 'None') {
      return true
    }
    if (facility.description) {
      return true
    }
    if (facility.photo && facility.photo.master_image) {
      return true
    }
    return false
  }
}

export default toNative(CampsiteFeatures)
</script>

<style scoped>
.popular-facilities,
.campsite-features {
  columns: 14ch 3;
  column-gap: 1em;
}

.popular-facilities {
  margin: 0 0 1em 0;
}

.popular-facilities .icon {
  width: 1.3em;
  height: 1.3em;
  margin-right: 0.2em;
}

.popular-facilities .popular {
  padding-left: 0;
}

.campsite-features h3 {
  margin: 0 0 0.25em 0;
  font-size: var(--body);
  color: #3a82a6;
  line-height: 1.1;
}

.campsite-features .box {
  break-inside: avoid-column;
  margin-bottom: 1em;
}

.campsite-features .active :deep(.info-button img) {
  opacity: 0.5;
}

p,
.facility {
  padding: 0.2em 0 0.3em 1em;
  margin: 0;
  line-height: 1.1;
  position: relative;
  font-size: var(--body);
  word-break: break-word;
  letter-spacing: -0.01em;
  display: block;
}

.feature-description {
  margin: 0.5em 0 0 0;
  padding: 0;
  line-height: 1.2;
  white-space: pre-line;
}

.feature-photo {
  width: 100%;
}

.campsite-features .facility:before {
  background: url('~/assets/images/tick.svg') no-repeat left 0.4em;
  content: '';
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 0.8em;
}

.campsite-features .facility.inactive:before {
  background: url('~/assets/images/not-allowed-grey.svg') no-repeat left 0.375em;
}

.campsite-features .facility.inactive {
  color: #b0b0b0;
}

/* desktop & tablet */
.desktop .popular-facilities {
  margin: 0px 0 2em 0;
}

.desktop .popular-facilities,
.desktop .campsite-features {
  columns: 21ch 5;
  column-gap: 2em;
}

.desktop .campsite-features h3 {
  font-size: revert;
}

.desktop p,
.desktop .facility {
  font-size: revert;
}

.desktop .campsite-features .box {
  margin-bottom: 2em;
}

.desktop .feature-description {
  font-size: inherit;
  line-height: 1.3;
}
</style>
